import * as React from "react";

export type ObservableResolutions = 'desktop' | 'tablet' | 'mobile';
export type ObservableSizes = {
  [key in ObservableResolutions]: {
    width: number;
    height: number;
  }
}

export interface AdProps {
  id: string;
  adUnit: string;
  generalSize: googletag.GeneralSize;
  refreshTimer?: number;
  targets?: Array<[string, string | string[]]>;
  minViewPercentage?: number;
  observableSizes?: ObservableSizes;
  isCentralized?: boolean;
  isResponsive?: boolean;
  sizeMapping?: Record<number | 'DEFAULT', googletag.GeneralSize>;
  onRequested?: (event: any) => void;
  onLoad?: (event: any) => void;
  onRenderEnded?: (event: any) => void;
  onImpressionViewable?: (event: any) => void;
  onVisibilityChanged?: (event: any) => void;
  onClick?: (event: any) => void;
}

export function Ad({
  id,
  adUnit,
  generalSize,
  refreshTimer,
  targets = [],
  minViewPercentage = 50,
  observableSizes,
  isCentralized = false,
  isResponsive = false,
  sizeMapping,
  onRequested,
  onLoad,
  onRenderEnded,
  onClick,
  onImpressionViewable,
  onVisibilityChanged,
}: AdProps) {
  /*
   * Hooks
   */
  const googletagRef = React.useRef<typeof googletag | null>(null);
  const adSlotRef = React.useRef<googletag.Slot | null>(null);
  const intervalRef = React.useRef<number | null>(null);
  const resizeObserverRef = React.useRef<ResizeObserver | null>(null);
  const [viewPercentage, setViewPercentage] = React.useState(0);

  function resizeUsingObservableSizes({ slot }: googletag.events.SlotRenderEndedEvent) {
    const parentNode = document.getElementById(slot.getSlotElementId());
    if (!parentNode) return;

    const wrapper = parentNode.getElementsByTagName('div')[0];
    const iframe = parentNode.getElementsByTagName('iframe')[0];
    if (!iframe || !wrapper) return;

    const sizes = observableSizes;
    const { desktop, tablet, mobile } = sizes;

    const maxSizes = {
      desktop: { width: desktop ? desktop.width : 0, height: desktop ? desktop.height : 0 },
      tablet: { width: tablet ? tablet.width : 0, height: tablet ? tablet.height : 0 },
      mobile: { width: mobile ? mobile.width : 0, height: mobile ? mobile.height : 0 },
    };
    
    let aspectRatio = maxSizes.desktop.height / maxSizes.desktop.width;

    wrapper.style.width = '100%'
    wrapper.style.height = '100%'
    iframe.width = `${wrapper.offsetWidth}px`
    iframe.height = `${wrapper.offsetWidth * aspectRatio}px`

    resizeObserverRef.current = new ResizeObserver((entries) => window.requestAnimationFrame(() => {
      if (!Array.isArray(entries) || !entries.length) {
        return
      }

      entries.forEach((entry) => {
        const { width } = entry.contentRect

        if (width <= maxSizes.mobile.width) {
          aspectRatio = maxSizes.mobile.height / maxSizes.mobile.width
        } else if (width > maxSizes.mobile.width && width <= maxSizes.tablet.width) {
          aspectRatio = maxSizes.tablet.height / maxSizes.tablet.width
        } else {
          aspectRatio = maxSizes.desktop.height / maxSizes.desktop.width
        }

        iframe.width = `${width}px`
        iframe.height = `${width * aspectRatio}px`
      })
    }));

    if (resizeObserverRef.current) {
      resizeObserverRef.current.observe(iframe)
    }
  }

  function applyResponsiveStyles(event: googletag.events.SlotRenderEndedEvent) {
    const parentNode = document.getElementById(event.slot.getSlotElementId());
    if (!parentNode) return;

    const iframe = parentNode.querySelector('iframe');
    if (!iframe || !iframe.contentWindow) return;

    const style = document.createElement('style');

    style.textContent = `
      #google_image_div {
        width: 100%;
      }

      #google_image_div img {
        width: 100%;
      }
    `;

    iframe.contentWindow.document.head.appendChild(style);
  }

  function getAdSizeForViewport() {
    if (!sizeMapping || Object.keys(sizeMapping).length === 0) {
      return generalSize;
    }

    const viewportWidth = window.innerWidth;

    const breakpoints = Object.keys(sizeMapping)
      .filter((key) => key !== 'DEFAULT')
      .map(Number)
      .sort((a, b) => b - a);

    for (const breakpoint of breakpoints) {
      if (viewportWidth >= breakpoint) {
        return sizeMapping[breakpoint];
      }
    }

    return sizeMapping['DEFAULT'] || generalSize;
  }

  function setTargeting(adSlot: googletag.Slot) {
    for (const [key, value] of targets) {
      adSlot.setTargeting(key, value);
    }
  }

  function setClickListener(event: googletag.events.SlotRenderEndedEvent) {
    if (!onClick) return;

    const parentNode = document.getElementById(event.slot.getSlotElementId());
    if (!parentNode) return;

    const iframe = parentNode.querySelector('iframe');
    if (!iframe) return;

    const { contentWindow } = iframe;
    if (!contentWindow) return;

    const handleClick = () => onClick(event);
    contentWindow.addEventListener('click', handleClick);
  }

  function setEvents(adSlot: googletag.Slot) {
    if (onRequested) {
      googletagRef.current.pubads().addEventListener("slotRequested", (event) => {
        if (event.slot === adSlot) {
          onRequested(event);
        }
      });
    }

    if (onLoad) {
      googletagRef.current.pubads().addEventListener("slotOnload", (event) => {
        if (event.slot === adSlot) {
          onLoad(event);
        }
      });
    }

    googletagRef.current.pubads().addEventListener("slotRenderEnded", (event) => {
      if (event.slot === adSlot) {
        if (onRenderEnded) {
          onRenderEnded(event);
        }

        if (observableSizes) {
          resizeUsingObservableSizes(event);
        }

        if (isResponsive) {
          applyResponsiveStyles(event);
        }

        setClickListener(event);
      }
    });

    googletagRef.current.pubads().addEventListener("slotVisibilityChanged", (event) => {
      if (event.slot === adSlot) {
        if (onVisibilityChanged) {
          onVisibilityChanged(event);
        }

        setViewPercentage(event.inViewPercentage);
      }
    });

    if (onImpressionViewable) {
      googletagRef.current.pubads().addEventListener("impressionViewable", (event) => {
        if (event.slot === adSlot) {
          onImpressionViewable(event);
        }
      });
    }
  }

  function createAdSlot(): googletag.Slot | null {
    if (typeof window !== "undefined") {
      if (adSlotRef.current) {
        googletagRef.current.destroySlots([adSlotRef.current]);
      }

      const adSize = getAdSizeForViewport();
      const adSlot = googletagRef.current.defineSlot(adUnit, adSize, id);

      if (adSlot) {
        adSlot.addService(googletagRef.current.pubads());
        adSlotRef.current = adSlot;
        return adSlot;
      }
    }

    return null;
  }

  function configureAdSlot(adSlot: googletag.Slot) {
    setEvents(adSlot);
    setTargeting(adSlot);
    googletagRef.current.pubads().enableSingleRequest();
    googletagRef.current.pubads().collapseEmptyDivs();
  }

  function initializeGoogleTag() {
    if (typeof window !== "undefined") {
      window.googletag = window.googletag || { cmd: [] } as typeof googletag;
      googletagRef.current = window.googletag;
    }

    window.googletag.cmd.push(() => {
      const adSlot = createAdSlot();

      if (adSlot) {
        configureAdSlot(adSlot);
        googletagRef.current.enableServices();
        googletagRef.current.display(id);
      }
    });
  }

  React.useEffect(() => {
    initializeGoogleTag();

    return () => {
      if (adSlotRef.current) {
        googletagRef.current.destroySlots([adSlotRef.current]);
      }

      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  React.useEffect(() => {
    if (refreshTimer && viewPercentage >= minViewPercentage) {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }

      intervalRef.current = window.setInterval(() => {
        if (adSlotRef.current) {
          googletagRef.current.pubads().refresh([adSlotRef.current]);
        }
      }, refreshTimer);
    }

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, [viewPercentage, minViewPercentage, refreshTimer]);

  return <div id={id} className="ad-container" style={isCentralized ? { width: 'fit-content', margin: '0 auto' } : {}} />;
}